import {
  Box,
  Button,
  Dialog,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { useCreate, useInvalidate, useList, useUpdate } from '@refinedev/core'
import { UUID } from 'crypto'
import { useState, useEffect } from 'react'
import { User } from '@/utils/auth/auth0'

interface AssignUserOpportunityModalProps {
  open: boolean
  onClose: () => void
  opportunity_uuid: UUID | null
  currentUser: User
  currentAssignedUser: string | null
}

interface UserOption {
  uuid: UUID | null
  name: string
}
export default function AssignUserOpportunityModal({
  open,
  onClose: handleCloseDialog,
  opportunity_uuid,
  currentUser,
  currentAssignedUser,
}: AssignUserOpportunityModalProps) {
  const [selectedUser, setSelectedUser] = useState<string>('')
  const { mutate: createAssignUserOpportunity } = useCreate()
  const { mutate: updateAssignUserOpportunity } = useUpdate()
  const invalidate = useInvalidate()

  useEffect(() => {
    setSelectedUser(currentAssignedUser ?? '')
  }, [currentAssignedUser])

  const { data: availableUsers, isLoading } = useList({
    resource: 'assigned-opportunities',
    meta: {
      get_users: true,
    },
  })

  if (!opportunity_uuid) {
    return null
  }

  function handleEditAssignUser() {
    if (!currentUser || currentUser.sub === null) {
      return
    }

    if (!opportunity_uuid) {
      return
    }

    updateAssignUserOpportunity(
      {
        resource: 'assigned-opportunities',
        values: {
          assigned_to_user_uuid: selectedUser,
        },
        id: opportunity_uuid,
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'assigned-opportunities',
            invalidates: ['all'],
          })
          invalidate({
            resource: 'opportunities',
            invalidates: ['all'],
          })

          handleCloseDialog()
        },
        onError: () => {
          alert('Error assigning user. Please try again.')
        },
      }
    )
  }

  function handleAddAssignUser() {
    if (selectedUser === '') {
      return
    }
    if (!currentUser || currentUser.sub === null) {
      return
    }

    if (!opportunity_uuid) {
      return
    }

    createAssignUserOpportunity(
      {
        resource: 'assigned-opportunities',
        values: {
          assigned_to_user_uuid: selectedUser,
          opportunity_uuid: opportunity_uuid,
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'opportunities',
            invalidates: ['all'],
          })
          handleCloseDialog()
        },
        onError: () => {
          alert('Error assigning user. Please try again.')
        },
      }
    )
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      maxWidth="sm"
      fullWidth
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Assign Someone to this opportunity{' '}
        </Typography>
        <Select
          sx={{ mb: 2, width: '100%' }}
          disabled={isLoading}
          value={selectedUser} // Use `selectedUser` instead
          label="User"
          onChange={(e) => setSelectedUser(e.target.value)}
        >
          <MenuItem value="">None</MenuItem>
          {availableUsers &&
            (availableUsers.data as UserOption[])
              .filter((user: UserOption) => user.uuid !== null)
              .map((user: UserOption) => (
                <MenuItem key={user.uuid} value={String(user.uuid)}>
                  {user.name}
                </MenuItem>
              ))}
        </Select>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {currentAssignedUser ? (
            <Button onClick={() => handleEditAssignUser()}>Update</Button>
          ) : (
            <Button onClick={() => handleAddAssignUser()}>Save</Button>
          )}
        </Box>
      </Box>
    </Dialog>
  )
}
